import { useEffect, useId, useRef, useState } from 'react';

import { copy as sharedCopy } from '@shared/copy';
import { Dialog } from '@shared/ui/dialog';

import { rejectCopy } from './copy';

/**
 * The rejection, from the list row and from the post alike — **one dialog, both
 * surfaces**, because the reason is part of the rejection rather than an
 * addition to it and a second dialog would drift from this one at the first
 * edit.
 *
 * **The reason is required, and the requirement is the point.** A post returned
 * to its writer with no reason leaves them guessing what would fix it, and they
 * re-submit it unchanged. The owning service refuses an empty one with its own
 * `422`; this field refuses it first so the round trip is not spent learning
 * that.
 *
 * Cancel is a `type="button"` and carries `formNoValidate`: both buttons in a
 * `method="dialog"` form are submits, a submit runs constraint validation, and
 * without the escape the dialog demands a value from an operator who has just
 * decided not to supply one.
 */
export function RejectDialog({
  open,
  authorName,
  busy = false,
  serverError,
  onReject,
  onCancel,
}: {
  open: boolean;
  /** Named where the surface knows it; the generic body stands in otherwise. */
  authorName?: string;
  busy?: boolean;
  serverError?: string;
  onReject: (reason: string) => void;
  onCancel: () => void;
}) {
  const headingId = useId();
  const fieldId = useId();
  const errorId = useId();
  const hintId = useId();
  const fieldRef = useRef<HTMLTextAreaElement>(null);

  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setTouched(false);
    window.requestAnimationFrame(() => fieldRef.current?.focus());
  }, [open]);

  const missing = touched && reason.trim().length === 0;

  return (
    <Dialog open={open} labelledBy={headingId} onClose={onCancel}>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setTouched(true);
          if (reason.trim().length === 0) {
            fieldRef.current?.focus();
            return;
          }
          onReject(reason);
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id={headingId} className="mb-2 font-display text-h3">
            {rejectCopy.heading}
          </h2>
          <p className="mb-0 max-w-none text-sm text-ink-muted">
            {authorName ? rejectCopy.bodyNamed(authorName) : rejectCopy.body}
          </p>
        </div>

        <div>
          <label htmlFor={fieldId} className="mb-2 block text-sm font-medium">
            {rejectCopy.label}
            <span aria-hidden="true" className="ms-0.5 text-danger">
              *
            </span>
          </label>
          <textarea
            id={fieldId}
            ref={fieldRef}
            rows={4}
            value={reason}
            aria-required="true"
            aria-invalid={missing ? true : undefined}
            aria-describedby={`${missing ? `${errorId} ` : ''}${hintId}`}
            placeholder={rejectCopy.placeholder}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
            className="w-full resize-y rounded-sm border border-border-strong bg-surface-raised p-4 text-body leading-normal text-ink placeholder:text-ink-faint focus-visible:border-accent"
          />
          {missing ? (
            <p id={errorId} className="mb-0 mt-2 max-w-none text-xs text-danger">
              {rejectCopy.required}
            </p>
          ) : null}
          <p id={hintId} className="mb-0 mt-2 max-w-none text-xs text-ink-muted">
            {rejectCopy.hint}
          </p>
        </div>

        {serverError ? (
          <p role="alert" className="mb-0 max-w-none text-sm text-danger">
            {serverError}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={busy}
            className="rounded-sm border border-danger px-5 py-3 text-sm font-semibold text-danger hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-45"
          >
            {rejectCopy.commit}
          </button>
          <button
            type="button"
            formNoValidate
            onClick={onCancel}
            className="rounded-sm px-5 py-3 text-sm font-semibold text-ink hover:bg-hover-bg"
          >
            {sharedCopy.action.cancel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
